import {
  createSystem,
  defineConfig,
  defaultConfig,
  RadioGroupLabel,
} from '@chakra-ui/react';
import { globalStyles } from './globalStyles';
import { buttonRecipe, iconButtonRecipe } from './components/button.recipe';
import { segmentGroupSlotRecipe } from './components/segmentGroup.recipe';

/**
 * Chakra UI v3 Theme Configuration
 *
 * This configuration defines semantic tokens that mirror the CSS custom properties
 * from the legacy Theme.tsx file. The tokens support both light and dark modes
 * using Chakra's _light and _dark conditions.
 */
const config = defineConfig({
  globalCss: globalStyles,
  conditions: {
    // Override Chakra's default color mode conditions
    // Dark mode is always the default (& matches everything)
    // Light mode only activates with explicit .light class
    light: '.light &, [data-theme="light"] &',
    dark: '&',
  },
  theme: {
    recipes: {
      button: buttonRecipe,
      iconButton: iconButtonRecipe,
    },
    slotRecipes: {
      segmentGroup: segmentGroupSlotRecipe,
    },
    semanticTokens: {
      colors: {
        // Primary brand colors
        primary: { value: 'rgb(0,90,112)' },
        accent: { value: '#46acb9' },
        accentLight: { value: '#7cdeed' },
        danger: {
          value: { base: 'rgb(244, 67, 54)', _light: '#f44336' },
        },
        warningAccent: { value: '#ffd740' },
        secondaryAccent: {
          value: { base: '#333333', _light: '#eee' },
        },
        darkText: { value: 'rgba(0,0,0, .88)' },
        lightText: { value: 'rgba(255,255,255, .72)' },
        // Text colors
        text: {
          value: {
            base: 'rgba(255,255,255, .72)',
            _light: 'rgba(0,0,0, .88)',
          },
        },
        secondaryText: {
          value: {
            base: 'rgba(255,255,255, .54)',
            _light: 'rgba(0,0,0, 0.54)',
          },
        },
        disabledText: {
          value: { base: 'rgba(255,255,255, .4)', _light: 'rgba(0,0,0, 0.3)' },
        },
        textShadow: {
          value: { base: '#000', _light: 'transparent' },
        },

        // Background colors
        background: {
          value: { base: '#121212', _light: '#455a64' },
        },
        cardBackground: {
          value: { base: '#282828', _light: '#fff' },
        },
        cardBackgroundInverse: { value: '#fff' },
        popupBackground: {
          value: { base: '#272727', _light: '#fff' },
        },

        // Loading indicator
        indicatorStartEnd: { value: 'rgba(25, 54, 54, 0.5)' },
        indicatorMid: { value: 'rgba(0, 0, 0, 0)' },

        // Scrollbar
        scrollBar: {
          value: { base: 'hsl(0, 0%, 20%)', _light: '#121212' },
        },

        // Menu colors
        menuBackground: {
          value: { base: '#212121', _light: 'rgb(99, 120, 130)' },
        },
        menuHoverBackground: { value: 'rgba(255, 255, 255, 0.1)' },
        menuText: {
          value: {
            base: 'rgba(255,255,255, .72)',
            _light: 'rgba(255,255,255, 1)',
          },
        },
        menuActiveBackground: {
          value: { base: 'rgb(0,90,112)', _light: 'rgba(158,158,158,0.4)' },
        },

        // List colors
        listBackground: {
          value: { base: '#1e1e1e', _light: '#fff' },
        },
        listBackgroundInverse: { value: '#fff' },
        listItemHover: {
          value: { base: '#2c2c2c', _light: '#eee' },
        },

        // Separator colors
        separator: {
          value: { base: 'hsl(0, 0%, 5%)', _light: 'rgba(0, 0, 0, 0.54)' },
        },
        separatorInverse: {
          value: {
            base: 'rgba(0, 0, 0, 0.12)',
            _light: 'rgba(0, 0, 0, 0.54)',
          },
        },

        // Button colors
        buttonBackground: {
          value: { base: 'rgb(0,90,112)', _light: 'rgba(158,158,158,0.2)' },
        },
        buttonText: {
          value: {
            base: 'rgba(255,255,255, .72)',
            _light: 'rgba(0,0,0, .88)',
          },
        },
        buttonDisabled: {
          value: {
            base: 'rgb(0, 70, 92)',
            _light: 'rgba(158, 158, 158, 0.2)',
          },
        },

        // Control colors
        controlBackground: {
          value: { base: 'hsl(0, 0%, 20%)', _light: '#eee' },
        },
        controlText: {
          value: {
            base: 'rgba(255,255,255, .72)',
            _light: 'rgba(0,0,0, .88)',
          },
        },

        // Metric value
        metricValue: {
          value: { base: '#46acb9', _light: '#000' },
        },

        // Graph colors
        graphBackground: {
          value: {
            base: 'linear-gradient(to bottom right, #121212 30%, #174859 100%)',
            _light: '#fff',
          },
        },
        graphGrid: {
          value: {
            base: 'rgba(255,255,255, 0.24)',
            _light: 'rgba(0,0,0, 0.24)',
          },
        },
        graphGridLines: {
          value: {
            base: 'rgba(204,204,204, 0.5)',
            _light: 'rgba(0,0,0, 0.24)',
          },
        },
        graphDotStroke: {
          value: { base: '#fff', _light: '#000' },
        },
        graphDotText: {
          value: { base: '#fff', _light: '#000' },
        },

        // Scatter plot colors
        scatterPrimary: {
          value: { base: 'yellow', _light: 'purple' },
        },
        scatterPrimaryInverse: { value: 'purple' },

        // Graph line colors
        graphLineOne: {
          value: { base: '#F6BD0F', _light: '#0f1e5c' },
        },
        graphLineTwo: {
          value: { base: '#DDDDDD', _light: '#d6355c' },
        },
        graphLineThree: {
          value: { base: '#478DB8', _light: '#ff7c2e' },
        },
        graphLineFour: {
          value: { base: '#8717ff', _light: '#f15648' },
        },

        // React Select colors
        reactSelectPrimary: {
          value: { base: 'hsl(192, 100%, 22%)', _light: '#2684FF' },
        },
        reactSelectPrimary75: {
          value: { base: 'hsl(192, 100%, 27%)', _light: '#4C9AFF' },
        },
        reactSelectPrimary50: {
          value: { base: 'hsl(192, 100%, 32%)', _light: '#B2D4FF' },
        },
        reactSelectPrimary25: {
          value: { base: 'hsl(192, 100%, 37%)', _light: '#DEEBFF' },
        },
        reactSelectDanger: { value: '#DE350B' },
        reactSelectDangerLight: { value: '#FFBDAD' },
        reactSelectNeutral0: {
          value: { base: 'hsl(0, 0%, 20%)', _light: 'hsl(0, 0%, 100%)' },
        },
        reactSelectNeutral5: {
          value: { base: 'hsl(0, 0%, 10%)', _light: 'hsl(0, 0%, 95%)' },
        },
        reactSelectNeutral10: {
          value: { base: 'hsl(0, 0%, 10%)', _light: 'hsl(0, 0%, 90%)' },
        },
        reactSelectNeutral20: {
          value: { base: 'hsl(0, 0%, 5%)', _light: 'hsl(0, 0%, 80%)' },
        },
        reactSelectNeutral30: {
          value: { base: 'hsl(0, 0%, 50%)', _light: 'hsl(0, 0%, 70%)' },
        },
        reactSelectNeutral40: {
          value: { base: 'hsl(0, 0%, 60%)', _light: 'hsl(0, 0%, 60%)' },
        },
        reactSelectNeutral50: {
          value: { base: 'hsl(0, 0%, 50%)', _light: 'hsl(0, 0%, 50%)' },
        },
        reactSelectNeutral60: {
          value: { base: 'hsl(0, 0%, 80%)', _light: 'hsl(0, 0%, 40%)' },
        },
        reactSelectNeutral70: {
          value: { base: 'hsl(0, 0%, 90%)', _light: 'hsl(0, 0%, 30%)' },
        },
        reactSelectNeutral80: {
          value: { base: 'hsl(0, 0%, 100%)', _light: 'hsl(0, 0%, 20%)' },
        },
        reactSelectNeutral90: {
          value: { base: 'hsl(0, 0%, 10%)', _light: 'hsl(0, 0%, 10%)' },
        },

        // Surface colors (elevation)
        surface0: {
          value: { base: '#121212', _light: '#fff' },
        },
        surface1: {
          value: { base: '#1e1e1e', _light: '#fff' },
        },
        surface2: {
          value: { base: '#222222', _light: '#fff' },
        },
        surface3: {
          value: { base: '#242424', _light: '#fff' },
        },
        surface6: {
          value: { base: '#2c2c2c', _light: '#fff' },
        },

        // Tooltip
        tooltip: { value: 'rgb(99, 99, 99)' },

        // Scoreboard
        scoreboardCard: {
          value: { base: 'darkslategrey', _light: '#fff' },
        },

        // Material 500 colors (static, no mode switching)
        materialRed: { value: '#F44336' },
        materialPink: { value: '#E91E63' },
        materialPurple: { value: '#9C27B0' },
        materialDeepPurple: { value: '#673AB7' },
        materialIndigo: { value: '#3F51B5' },
        materialBlue: { value: '#2196F3' },
        materialLightBlue: { value: '#18FFFF' },
        materialCyan: { value: '#00BCD4' },
        materialTeal: { value: '#009688' },
        materialGreen: { value: '#4CAF50' },
        materialLightGreen: { value: '#8BC34A' },
        materialLime: { value: '#CDDC39' },
        materialYellow: { value: '#FFEB3B' },
        materialAmber: { value: '#FFC107' },
        materialOrange: { value: '#FF9800' },
        materialDeepOrange: { value: '#FF5722' },
        materialBrown: { value: '#795548' },
        materialGray: { value: '#9E9E9E' },
        materialBlueGray: { value: '#607D8B' },
      },
      shadows: {
        elevation0: { value: 'none' },
        elevation1: {
          value:
            '0 1px 1px 0 rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12), 0 1px 3px 0 rgba(0,0,0,0.20)',
        },
        elevation2: {
          value:
            '0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12), 0 1px 5px 0 rgba(0,0,0,0.20)',
        },
        elevation3: {
          value:
            '0 3px 4px 0 rgba(0,0,0,0.14), 0 3px 3px -2px rgba(0,0,0,0.12), 0 1px 8px 0 rgba(0,0,0,0.20)',
        },
        elevation4: {
          value:
            '0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12), 0 2px 4px -1px rgba(0,0,0,0.20)',
        },
        elevation6: {
          value:
            '0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12), 0 3px 5px -1px rgba(0,0,0,0.20)',
        },
        elevation8: {
          value:
            '0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12), 0 5px 5px -3px rgba(0,0,0,0.20)',
        },
        elevation12: {
          value:
            '0 12px 17px 2px rgba(0,0,0,0.14), 0 5px 22px 4px rgba(0,0,0,0.12), 0 7px 8px -4px rgba(0,0,0,0.20)',
        },
        elevation16: {
          value:
            '0 16px 24px 2px rgba(0,0,0,0.14), 0 6px 30px 5px rgba(0,0,0,0.12), 0 8px 10px -5px rgba(0,0,0,0.20)',
        },
        elevation24: {
          value:
            '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.20)',
        },
      },
      spacing: {
        gridGap: { value: '15px' },
      },
      sizes: {
        graphMinHeight: { value: '400px' },
      },
    },
  },
});

export const system = createSystem(defaultConfig, config);
